


















































import Vue from "vue";
import ModalDlg from "@/components/ModalDlg.vue";
import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import { useStore } from "@/store/store";
import { mapStores } from "pinia";
import { BvModalEvent } from "bootstrap-vue";
import {drawSoundOnCanvas, getRMS, audioBufferToDataURL} from "@/helpers/media";
import {TranslateResult} from "vue-i18n";
import {isMacOSPlatform} from "@/helpers/common";

const previewImageWidth = 300;
const previewImageHeight = 100;

export default Vue.extend({
    name: "EditSoundDlg",

    components:{
        Cropper,
        ModalDlg,
    },

    props:{
        dlgId: String,
        dlgTitle: String,
        soundToEdit: AudioBuffer,
    },
    
    data: function() {
        return {
            imgPreview: "",
            currentSoundLength: "Loading...",
            // We want to allow 0.2 to 5.0 multiplier
            // The actual value on the slider goes from -70 to +70
            // If you put this through 10^(v/100), this turns into roughly 0.2 to 5
            volumeScaleLogPercent: 0,
            volumeRMS: 0,
            crop: {firstSampleIncl: 0, lastSampleExcl: 0, leftPixel: 0, widthPixels: 0},
            stopPreview: null as (() => void) | null,
            cursorTime: undefined as string | undefined,
            cursorHeight: undefined as string | undefined,
        };
    },

    created() {
        // Register the event listener for the dialog here
        this.$root.$on("bv::modal::hide", this.onHideModalDlg);
    },

    beforeDestroy(){
        // Remove the event listener for the dialog here, just in case...
        this.$root.$off("bv::modal::hide", this.onHideModalDlg);
    },

    mounted() {
        window.addEventListener("keydown", this.onKeyDown);
    },

    destroyed() {
        window.removeEventListener("keydown", this.onKeyDown);
    },

    computed:{
        ...mapStores(useStore),

        dlgMsg(): string{
            return this.appStore.simpleModalDlgMsg;
        },
        playStopLabel(): TranslateResult {
            return this.stopPreview == null ? this.$t("media.soundPlay") : this.$t("media.soundStop");
        },
        playStopVariant() : string {
            return this.stopPreview == null ? "success" : "danger";
        },
    },

    methods:{
        isMacOSPlatform,
        onHideModalDlg(event: BvModalEvent, id: string){
            if (this.stopPreview != null) {
                this.stopPreview();
            }
        },
        defaultSize({imageSize, visibleArea} : { imageSize: {width: number, height: number}, visibleArea : {width: number, height: number} }) {
            return {
                width: (visibleArea || imageSize).width,
                height: (visibleArea || imageSize).height,
            };
        },
        change(info : {image: any, coordinates: {left: number, top: number, width: number, height: number}}) {
            this.crop = {
                firstSampleIncl: Math.round(info.coordinates.left / previewImageWidth * this.soundToEdit?.length),
                lastSampleExcl: Math.round((info.coordinates.left + info.coordinates.width) / previewImageWidth * this.soundToEdit?.length),
                leftPixel: info.coordinates.left,
                widthPixels: info.coordinates.width,
            };
            // Changing the crop changes the average volume:
            let volumeFactor = 10 ** (this.volumeScaleLogPercent / 100.0);
            this.volumeRMS = getRMS(this.soundToEdit, volumeFactor, this.crop.firstSampleIncl, this.crop.lastSampleExcl);
            this.currentSoundLength = ((this.crop.lastSampleExcl - this.crop.firstSampleIncl - 1) / this.soundToEdit?.sampleRate).toFixed(3);
        },
        doNormaliseVolume() {
            let rms = getRMS(this.soundToEdit, 1.0, this.crop.firstSampleIncl, this.crop.lastSampleExcl);
            this.volumeScaleLogPercent = Math.max(-70, Math.min(70, Math.round(Math.log10(rms == 0.0 ? 1 : 0.1 / rms) * 100))); 
        },
        doPlayStopPreview() {
            if (this.stopPreview != null) {
                this.stopPreview();
                return;
            }
            
            // Add a play marker:
            const playbackLine = document.createElement("div");
            playbackLine.className = "EditSoundDlg-img-red-line";
            playbackLine.setAttribute("style", "opacity: 0%; left: 0%;");
            
            document.getElementsByClassName("vue-preview__wrapper")?.[0].append(playbackLine);
            
            // We are handling a user-triggered click event, which allows us to play sound:
            const ctx = new AudioContext();
            const src = ctx.createBufferSource();
            src.buffer = this.soundToEdit;
            var gainNode = ctx.createGain();
            const volumeFactor = 10 ** (this.volumeScaleLogPercent / 100.0);
            gainNode.gain.value = volumeFactor;
            gainNode.connect(ctx.destination);

            src.connect(gainNode);
            const startTime = ctx.currentTime;
            let updater = null as number | null;
            this.stopPreview = () => {
                src.stop();
                if (updater != null) {
                    window.clearInterval(updater);
                    updater = null;
                }
                playbackLine.remove();
                this.stopPreview = null;
            };
            src.onended = this.stopPreview;
            let sampleDuration = this.crop.lastSampleExcl - this.crop.firstSampleIncl;
            src.start(0, this.crop.firstSampleIncl / this.soundToEdit?.sampleRate, sampleDuration / this.soundToEdit?.sampleRate);
            
            // Show a red marker animating across as it plays.
            // There isn't a way to get regular callbacks while playing
            // so we must time it ourselves.  We don't bother if the sound is under 300ms:
            updater = window.setInterval(() => {
                const percentage = (ctx.currentTime - startTime) / (sampleDuration / this.soundToEdit.sampleRate) * 100;
                if (percentage >= 100) {
                    this.stopPreview?.();
                }
                else {
                    playbackLine.style.left = percentage + "%";
                    playbackLine.style.opacity = "100%";
                }
            }, 100);
        },
        getUpdatedMedia() : Promise<{code: string, mediaType: string}> {
            const copiedAudioBuffer = new AudioBuffer({
                length: this.crop.lastSampleExcl - this.crop.firstSampleIncl,
                numberOfChannels: this.soundToEdit.numberOfChannels,
                sampleRate: this.soundToEdit.sampleRate,
            });

            const volumeFactor = 10 ** (this.volumeScaleLogPercent / 100.0);
            for (let channel = 0; channel < this.soundToEdit.numberOfChannels; channel++) {
                const channelData = this.soundToEdit.getChannelData(channel);
                const copiedChannelData = copiedAudioBuffer.getChannelData(channel);

                for (let sample = this.crop.firstSampleIncl; sample < this.crop.lastSampleExcl; sample++) {
                    copiedChannelData[sample - this.crop.firstSampleIncl] = channelData[sample] * volumeFactor;
                }
            }
            
            return audioBufferToDataURL(copiedAudioBuffer).then((dataURL) => {
                return {
                    code: "load_sound(\"" + dataURL + "\")", 
                    mediaType: "audio/wav",
                };
            });
        },
        handleMouseMove(event: MouseEvent) {
            const cropper = this.$refs.cropper as Cropper;
            const imageElement = cropper?.$el.querySelector("img");

            if (cropper && imageElement && imageElement.complete) {
                const rect = imageElement.getBoundingClientRect();
                const offsetX = event.clientX - rect.left;
                const offsetY = event.clientY - rect.top;

                const scaleX = imageElement.width / imageElement.getBoundingClientRect().width;
                const scaleY = imageElement.height / imageElement.getBoundingClientRect().height;

                const imageX = offsetX * scaleX;
                const imageY = offsetY * scaleY;

                if (imageX >= 0 && imageX < imageElement.width && imageY >= 0 && imageY < imageElement.height) {
                    const t = (imageX / previewImageWidth) * this.soundToEdit?.duration;
                    this.cursorTime = t != undefined ? t.toPrecision(3) : undefined;
                    this.cursorHeight = ((imageY / previewImageHeight) * -2 + 1).toFixed(2);
                }
                else {
                    this.cursorTime = undefined;
                    this.cursorHeight = undefined;
                }
            }
        },

        onKeyDown(event: KeyboardEvent) {
            if (this.$refs.cropper && event.key === "c" && ((isMacOSPlatform() && event.metaKey) || (!isMacOSPlatform() && event.ctrlKey))) {
                if (event.shiftKey) {
                    if (this.cursorHeight !== undefined) {
                        navigator.clipboard.writeText(this.cursorHeight);
                    }
                }
                else {
                    if (this.cursorTime !== undefined) {
                        // We don't copy the outer brackets:
                        navigator.clipboard.writeText(this.cursorTime);
                    }
                }
                event.stopPropagation();
                event.stopImmediatePropagation();
                event.preventDefault();
            }
        },
    },
    watch: {
        volumeScaleLogPercent() {
            // Redraw sound with new volume:
            let volumeFactor = 10 ** (this.volumeScaleLogPercent / 100.0);
            this.imgPreview = drawSoundOnCanvas(this.soundToEdit, previewImageWidth, previewImageHeight, volumeFactor);
            this.volumeRMS = getRMS(this.soundToEdit, volumeFactor, this.crop.firstSampleIncl, this.crop.lastSampleExcl);
            // But retain same crop (have to do this after cropper has updated the image):
            Vue.nextTick(() => {
                (this.$refs.cropper as Cropper).setCoordinates({
                    left: this.crop.leftPixel,
                    width: this.crop.widthPixels,
                    top: 0,
                    height: previewImageHeight,
                });
            });
        },
        soundToEdit() {
            // When image changes (dialog being re-shown), redraw image:
            if (this.soundToEdit != null) {
                let volumeFactor = 10 ** (this.volumeScaleLogPercent / 100.0);
                this.imgPreview = drawSoundOnCanvas(this.soundToEdit, previewImageWidth, previewImageHeight, volumeFactor);
                this.volumeRMS = getRMS(this.soundToEdit, volumeFactor);
                // Reset volume scale:
                this.volumeScaleLogPercent = 0;
                this.crop = {firstSampleIncl: 0, lastSampleExcl: this.soundToEdit.length, leftPixel: 0, widthPixels: previewImageWidth};
                this.currentSoundLength = this.soundToEdit.duration.toFixed(3); 
            }
        },
    },
});
